import React, { useMemo } from 'react';

import type { SummaryOfRegulation } from 'app/hooks/api/regulations/useRegulationsGetRegulationsQuery';

import { RegulationsFieldsPanel } from '../RegulationsFieldsPanel';
import { useRegulationsFormikRef } from '../Fields';
import type { RegulationsField, RegulationsPanelColumn, RegulationsPanelRow } from '../Fields';

import type { RegulationsFormGroup, RegulationsFromGroupKeys } from '../RegulationsPanelForm';

import { RegulationsRevertConfirmDialog } from './RegulationsRevertConfirmDialog';

type ExtendedOptionsInput = SummaryOfRegulation['options']['input'] & { localDate: boolean };

type ExtendedField = RegulationsField & { onReset?: () => void };

type Label =
  | {
      name: string;
      defaultValue: string | undefined;
      variant: RegulationsField['variant'];
      required: boolean;
      disabled: boolean;
      onReset?: () => void;
      highlight?: boolean;
    }
  | undefined;

type ResetConfirmationState = {
  fieldName: string;
  currentValue: any;
  defaultValue: any;
  label?: string;
  fieldType?: RegulationsField['type'];
  fieldOptions?: RegulationsField['options'];
};

type RegulationsPanelProps = {
  header?: string | React.ReactElement;
  regulations: RegulationsFormGroup;
  loading: boolean;
  isEmployer?: boolean;
  editing: boolean;
};

export const RegulationsPanel = ({
  header,
  regulations,
  loading,
  isEmployer = false,
  editing,
}: RegulationsPanelProps) => {
  const formikRef = useRegulationsFormikRef();

  const [resetConfirmation, setResetConfirmation] = React.useState<ResetConfirmationState | null>(null);

  const closeResetConfirmation = React.useCallback(() => {
    setResetConfirmation(null);
  }, []);

  const confirmResetValue = React.useCallback(() => {
    if (!resetConfirmation) {
      return;
    }

    const formik = formikRef.current;
    if (!formik) {
      setResetConfirmation(null);
      return;
    }

    if (JSON.stringify(formik.initialValues) !== JSON.stringify(formik.values)) {
      formik.resetForm();
    }
    formik.setFieldValue(resetConfirmation.fieldName, resetConfirmation.defaultValue);
    formik.submitForm();
    setResetConfirmation(null);
  }, [formikRef, resetConfirmation]);

  const onResetValueButtonClick = React.useCallback(
    (
        fieldName: string,
        defaultValue: any,
        currentValue: any,
        label?: string,
        fieldType?: RegulationsField['type'],
        fieldOptions?: RegulationsField['options'],
      ) =>
      () => {
        setResetConfirmation({ fieldName, defaultValue, currentValue, label, fieldType, fieldOptions });
      },
    [],
  );

  const columns = useMemo<Array<RegulationsPanelColumn>>(() => {
    const initialValues = formikRef.current?.initialValues ?? {};

    return Object.keys(regulations).map(groupName => {
      const rows: Array<RegulationsPanelRow> = (regulations[groupName as RegulationsFromGroupKeys] ?? []).map(value => {
        const inputOptions = value.options?.input || {};
        const items = inputOptions?.options || [];

        const editableLabels = !isEmployer;

        const isEditableLabelAndHasBeenChanged =
          editableLabels && !value.usedInCalculations && value.description !== value.defaultDescription;

        const fieldInputName = `${value.summaryOfRegulationId}.value`;

        const field: ExtendedField = {
          type: value.fieldType ?? 'text',
          name: fieldInputName,
          variant: 'slim',
          value: value.value,
          options: inputOptions,
          items: items,
          required: value.usedInCalculations ?? false,
        };

        if (field.type === 'date') {
          field.options = {
            ...field.options,
            localDate: true, // needed to ensure that all dates are interpreted (and stored) as user local dates
          } as ExtendedOptionsInput;
        }

        if (value.flagged) {
          field.highlight = isEmployer;
          field.onReset = isEmployer
            ? onResetValueButtonClick(
                fieldInputName,
                value.defaultValue,
                value.value,
                value.description,
                field.type,
                field.options,
              )
            : undefined;
        }

        let label: Label = undefined;
        if (editableLabels) {
          const labelInputName = `${value.summaryOfRegulationId}.description`;

          label = {
            name: labelInputName,
            defaultValue: initialValues[value.summaryOfRegulationId]?.description,
            variant: 'slim',
            required: true,
            disabled: value.usedInCalculations,
          };

          if (isEditableLabelAndHasBeenChanged) {
            label.onReset = onResetValueButtonClick(
              labelInputName,
              value.defaultDescription,
              value.description,
              value.description,
            );
          }
        }

        return {
          key: value.description,
          value: {
            name: field.name,
            editable: editing,
            field,
            label,
          },
        };
      });

      return {
        title: groupName,
        rows,
      };
    });
  }, [isEmployer, editing, onResetValueButtonClick, regulations, formikRef]);

  const rowCount = columns.reduce((count, column) => count + column.rows.length, 0);

  if (!rowCount) {
    return null;
  }

  return (
    <>
      <RegulationsFieldsPanel header={header} columns={columns} loading={loading} />
      <RegulationsRevertConfirmDialog
        open={!!resetConfirmation}
        label={resetConfirmation?.label}
        currentValue={resetConfirmation?.currentValue}
        defaultValue={resetConfirmation?.defaultValue}
        fieldType={resetConfirmation?.fieldType}
        fieldOptions={resetConfirmation?.fieldOptions}
        onClose={closeResetConfirmation}
        onConfirm={confirmResetValue}
      />
    </>
  );
};
